<template>
  <div class="analysis-studio">
    <ProgressView v-if="isRequestInProgress" />
    <TopBar
      @showStats="showStats = true"
      @showMap="showStats = false"
      @toggleSearch="toggleGeoSearch"
      :showStats="showStats"
    />
    <div class="content-area">
      <DeckGL
        :accessToken="accessToken"
        :layers="returnLayers"
        :latitude="latitude"
        :longitude="longitude"
        :zoom="zoom"
        :updateViewStateOnLayersChange="false"
        @onClick="handleClick"
        @onDragStart="closePopUp"
      />
      <LayersPanel />
      <DetailsPanel />
      <TimeLineView />
      <SlideYDownTransition>
        <StatsView v-show="showStats" @close="showStats = false" />
      </SlideYDownTransition>
    </div>

    <SlideYUpTransition>
      <GeoSearch
        v-if="showGeoSearch"
        :entities="[]"
        @clickClose="toggleGeoSearch"
        @select="handleGeoSelection"
      />
    </SlideYUpTransition>
    <GeoPopup
      v-if="popUpData && popUpData.show"
      :icon="popUpData.icon"
      :title="popUpData.title"
      :value="popUpData.value"
      :metadata="popUpData.metadata"
      :pinned="popUpData.pinned"
      :x="popUpData.x"
      :y="popUpData.y"
    />
    <ContextMenu :data="contextData" @close="closeContext" />
  </div>
</template>

<script>
import DeckGL from "@/components/global/views/DeckGL";
import TopBar from "@/components/supply_demand_gap/containers/TopBar";
import LayersPanel from "@/components/supply_demand_gap/containers/LayersPanel";
import DetailsPanel from "@/components/supply_demand_gap/containers/DetailsPanel";
import TimeLineView from "@/components/supply_demand_gap/containers/TimeLineView";
import StatsView from "@/components/supply_demand_gap/containers/StatsView";
import { SlideYDownTransition, SlideYUpTransition } from "vue2-transitions";
import { ScatterplotLayer, TextLayer } from "@deck.gl/layers";
import { WebMercatorViewport } from "@deck.gl/core";
import { mapGetters, mapActions } from "vuex";
import GeoSearch from "@/modules/GeoSearch";
import GeoPopup from "@/modules/GeoPopup";
import ContextMenu from "@/components/global/views/ContextMenu";
import City from "@/models/City.model";
import H3Layer from "@/layers/H3Layer";
import { H3HexagonLayer } from "@deck.gl/geo-layers";
import chroma, { limits, scale } from "chroma-js";
import { joinHexGeoData } from "@/lib/joinHexGeoData";

export default {
  components: {
    TopBar,
    DeckGL,
    LayersPanel,
    DetailsPanel,
    TimeLineView,
    StatsView,
    SlideYDownTransition,
    SlideYUpTransition,
    GeoSearch,
    ContextMenu,
    GeoPopup,
  },
  data() {
    return {
      accessToken:
        "pk.eyJ1IjoiaGF4emllIiwiYSI6ImNqZ2c2NWp3OTBxenAzM3FzeThydmZ0YncifQ.584ugILuKFfDPTxqyiO_0g",
      showStats: false,
      selectedStaticEntities: [],
      popUpData: {},
      contextData: {},
      latitude: 12.9141,
      longitude: 74.856,
      zoom: 12,
      layers: [],
      showGeoSearch: false,
      watchActiveCity: true,
    };
  },
  computed: {
    ...mapGetters("Data", ["isRequestInProgress"]),
    ...mapGetters("Layers", ["getActiveLayers", "getActiveLayerDetails"]),
    ...mapGetters("Location", ["getGeoDataById"]),
    ...mapGetters("SupplyDemand", ["getLayerTypes"]),
    // generates the list of layers needs to be renderd for the current group
    returnLayers() {
      let layers = [];

      if (
        !(
          this.getLayerTypes &&
          this.getActiveLayers &&
          Object.entries(this.getActiveLayers).length == 2
        )
      )
        return [];

      const {
        SUPPLY: supplyLayerType,
        DEMAND: demandLayerType,
      } = this.getLayerTypes;

      // Both Supply and Demand could be any
      if (supplyLayerType === "H3Hexagon" && demandLayerType === "H3Hexagon") {
        let {
          SUPPLY: supplyLayer,
          DEMAND: demandLayer,
        } = this.getActiveLayerDetails;

        // 1) Get the Data
        let supplyLayerGeoData = this.getGeoDataById(supplyLayer.id); // [ [geoId, sValue] ]
        let demandLayerGeoData = this.getGeoDataById(demandLayer.id); // [ [geoId, dValue] ]

        if (
          !(
            supplyLayerGeoData &&
            supplyLayerGeoData.length > 0 &&
            demandLayerGeoData &&
            demandLayerGeoData.length > 0
          )
        )
          return [];

        // 2) Join the Data ([geoId, sValue, dValue, sValue - dValue])
        let joinedData = joinHexGeoData(supplyLayerGeoData, demandLayerGeoData);

        // Demand to Supply (Negative Diff is towards left)
        // const colors = scale(["#00ff87", "#00d8ff"])
        const colors = scale(["#fc466b", "#3f5efb"])
          .mode("lch")
          .colors(5)
          .map(item => chroma(item).rgb());
        console.log(colors);

        // TODO: Cache these in the store:
        // Generate color buckets
        const colorBuckets = limits(
          joinedData
            .slice(1, joinedData.length)
            // .map(d => d[3]) // Based on supplyValue - demandValue
            .map(d => d[2]) // Based on demandValue
            .filter(item => item !== 0),
          "e",
          10
        );
        console.log("colorBuckets ", colorBuckets);

        if (joinedData && joinedData.length > 0) {
          let gridLayer = new H3HexagonLayer({
            id: supplyLayer.id + demandLayer.id,
            data: joinedData.slice(1, joinedData.length),
            //dataTransform: data => data.filter(item => item[1] >= minPercentage && item[1] <= maxPercentage),
            pickable: true,
            highPrecision: true,
            wireframe: false,
            filled: true,
            extruded: true,
            elevationScale: 20,
            getHexagon: d => d[0], // first element is the hexagon/grid id
            // getElevation: d => Math.abs(d[3]), // Depends on sValue - dValue
            getElevation: d => Math.abs(d[1]), // Depends on sValue
            // getElevation: d => d[2], // Depends on dValue
            getFillColor: d => {
              return colors[this.getRangeIndex(colorBuckets, d[2])];
            },
            getLineColor: () => [255, 255, 255, 0], // transparent grid border
            onHover: (info, event) => {
              // If the popup is pinned, don't listen to hover events:
              if (this.popUpData.pinned) return;

              // If hovering over a static entity, show the popup:
              if (info.picked && info.object) {
                const {
                  center: { x, y },
                } = event;
                const { object } = info;
                this.setPopUpData({
                  show: true,
                  x,
                  y,
                  metadata: {
                    [demandLayer.name]: object[1],
                    [supplyLayer.name]: object[1],
                  },
                  title: "Grid Id",
                  value: object[0],
                  icon: "bubble_chart",
                });
              } else {
                // If not hovering over a static entity, hide the popup:
                this.setPopUpData({
                  show: false,
                });
              }
            },
            onClick: (info, event) => {
              // If clicked on a static entity, pin the popup:
              if (info.picked && info.object) {
                const {
                  center: { x, y },
                } = event;
                const { object } = info;
                this.setPopUpData({
                  show: true,
                  x,
                  y,
                  metadata: {
                    [demandLayer.name]: object[1],
                    [supplyLayer.name]: object[1],
                  },
                  title: "Grid Id",
                  value: object[0],
                  icon: "bubble_chart",
                  pinned: true,
                });
              }
            },
          });
          layers.push(gridLayer);
        }

        // 3) Generate the layer using joinedData
      } else {
        // Depending on the type of layer do the thing:
        let activeLayers = this.getActiveLayerDetails;

        Object.keys(activeLayers).forEach(layerLevel => {
          let Layer = activeLayers[layerLevel];
          let data = this.getGeoDataById(Layer.id);

          switch (this.getLayerTypes[Layer.type]) {
            case "Scatterplot": {
              let pointLayer = new ScatterplotLayer({
                id: `${Layer.id} location-point`,
                data: data,
                pickable: true,
                opacity: 0.8,
                stroked: false,
                filled: true,
                radiusScale: 6,
                radiusMinPixels: 0.5,
                radiusMaxPixels: 100,
                // lineWidthMinPixels: 20,
                getPosition: d => [d[1], d[0]],
                getRadius: d => {
                  // d = [lat, long, value, normalizedValue]
                  return 10 + d[3];
                },
                getFillColor: d => {
                  // return [255, 151, 10, 200];
                  return [0, 255, 164, 100];
                },
                getLineColor: d => [3, 165, 252, 100],
              });
              layers.push(pointLayer);
              break;
            }
            case "H3Hexagon": {
              if (data && data.length > 0) {
                console.log(data);
                let gridLayer = new H3HexagonLayer({
                  id: Layer.id,
                  data: data.slice(1, data.length),
                  //dataTransform: data => data.filter(item => item[1] >= minPercentage && item[1] <= maxPercentage),
                  pickable: true,
                  highPrecision: true,
                  wireframe: false,
                  filled: true,
                  extruded: !true,
                  elevationScale: 20,
                  getHexagon: d => d[0], // first element is the hexagon/grid id
                  getElevation: d => d[1],
                  getFillColor: d => {
                    return [255, (1 - d[1] / 500) * 255, 0];
                  },
                  getLineColor: () => [255, 255, 255, 0], // transparent grid border
                });
                layers.push(gridLayer);
              }
              break;
            }
          }
        });
      }
      return layers;
    },
  },
  methods: {
    ...mapActions("Location", ["fetchCityForLatLong", "setActiveCity"]),
    ...mapActions("SupplyDemand", ["refetchAllWidgets"]),
    getRangeIndex(range, item) {
      for (let i = 0; i < range.length - 1; i++) {
        if (item >= range[i] && item <= range[i + 1]) {
          return i;
        }
      }
    },
    toggleGeoSearch() {
      this.showGeoSearch = !this.showGeoSearch;
    },
    handleGeoSelection(selection) {
      if (selection.selection_type && selection.selection_type.mode) {
        switch (selection.selection_type.mode) {
          case "mapbox":
            this.zoom = selection.selection_type.zoom || 12;
            [this.latitude, this.longitude] = [
              ...selection.selection_type.location,
            ].reverse();
            this.setCityforLatLong(
              selection.selection_type.location,
              selection.bbox || [selection.center] || [
                  selection.selection_type.location,
                ]
            );
            break;
          case "coordinates":
            this.zoom = selection.selection_type.zoom || 16;
            [this.latitude, this.longitude] = [
              ...selection.selection_type.location,
            ].reverse();
            this.setCityforLatLong(selection.selection_type.location, null);
            break;
          case "entity":
            this.zoom = selection.selection_type.zoom || 16;
            [this.latitude, this.longitude] = [...selection.geo];
            this.setCityforLatLong([...selection.geo].reverse(), null);
            break;
        }
      }
    },
    async setCityforLatLong(latlong, bbox) {
      const city = await this.fetchCityForLatLong(latlong);
      const cityModel = new City({
        city_id: city.city_id,
        city: city.city,
        state: city.state || null,
        bbox: city.bbox || bbox || [latlong], // set the bbox to any of the mentioned
        latlong: latlong.reverse(),
        zoom: 12,
      });
      if (city) {
        this.setActiveCity(cityModel);
        this.refetchAllWidgets();
      }
    },
    setPopUpData(data) {
      this.popUpData = data;
    },
    setContextData(data) {
      this.contextData = data;
    },
    handleClick(info, event) {
      // If not clicked on Static Entity, close the popup:
      if (!info.picked) this.closePopUp();

      // If rightclicked, show copy coordinates context menu:
      if (event && event.rightButton) {
        const { coordinate, x, y } = info;
        this.setContextData({
          x,
          y: y + 60, // Added offset for the topbar
          data: {
            coordinate,
          },
          show: true,
        });
      } else {
        this.setContextData({ show: false });
      }
    },
    closePopUp() {
      this.setPopUpData({ show: false });
    },
    closeContext() {
      this.setContextData({ show: false });
    },
  },
  watch: {
    getActiveCity: {
      immediate: true,
      handler(city) {
        if (!city) return;
        if (this.watchActiveCity) {
          if (city.latlong && city.zoom) {
            // Just update lat long and zoom if present, for the first time
            this.zoom = city.zoom;
            this.latitude = city.latlong[0];
            this.longitude = city.latlong[1];
          } else {
            console.error(
              "lat_lon and zoom_level are not present in the console city meta"
            );
          }
          this.watchActiveCity = false;
        }
      },
    },
  },
  created: function() {},
  mounted: function() {},
};
</script>

<style lang="scss" scoped>
.analysis-studio {
  display: flex;
  position: relative;
  flex-direction: column;
  height: 100%;

  .top-bar {
    height: 66px;
    background: var(--color-secondary);
  }

  .content-area {
    flex: 1;
    position: relative;
    height: 100%;
    background: var(--color-primary);
  }
}
</style>
